import { ChangeDetectionStrategy, Component, ElementRef, OnDestroy, OnInit, output, signal, viewChild } from '@angular/core';

const CANVAS_WIDTH = 320;
const CANVAS_HEIGHT = 240;
const SHIP_SIZE = 10;
const ROT_SPEED = 0.09;
const THRUST = 0.08;
const FRICTION = 0.99;
const MAX_SPEED = 4;
const BULLET_SPEED = 5;
const BULLET_LIFE = 45;
const SHOOT_COOLDOWN_MS = 250;
const START_LIVES = 3;
const TICK_MS = 33;
const INVULN_TICKS = 60;

const SIZE_RADIUS: Record<AsteroidSize, number> = { large: 22, medium: 13, small: 7 };
const SIZE_SPEED: Record<AsteroidSize, number> = { large: 0.5, medium: 0.9, small: 1.5 };
const SIZE_POINTS: Record<AsteroidSize, number> = { large: 20, medium: 50, small: 100 };
const NEXT_SIZE: Record<AsteroidSize, AsteroidSize | null> = { large: 'medium', medium: 'small', small: null };

type AsteroidSize = 'large' | 'medium' | 'small';
type GameStatus = 'playing' | 'won' | 'lost';

interface Bullet {
  x: number;
  y: number;
  vx: number;
  vy: number;
  life: number;
}

interface Asteroid {
  x: number;
  y: number;
  vx: number;
  vy: number;
  size: AsteroidSize;
  radius: number;
  angle: number;
  spin: number;
  jitters: number[];
}

const MOVE_KEYS = ['arrowup', 'arrowleft', 'arrowright', 'w', 'a', 'd'];

@Component({
  selector: 'app-asteroids-game',
  host: {
    role: 'dialog',
    'aria-modal': 'true',
    'aria-label': 'Asteroids mini-game',
    '(document:keydown)': 'onKeydown($event)',
    '(document:keyup)': 'onKeyup($event)'
  },
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <div class="asteroids-panel">
      <div class="asteroids-header">
        <span class="asteroids-title">*** ASTEROIDS.EXE ***</span>
        <span class="asteroids-score">SCORE: {{ score() }}</span>
        <span class="asteroids-lives">LIVES: {{ lives() }}</span>
        <button class="asteroids-close" type="button" (click)="requestClose()">[ESC] CLOSE</button>
      </div>
      <canvas #canvasEl [width]="canvasWidth" [height]="canvasHeight" class="asteroids-canvas"></canvas>
      @if (status() !== 'playing') {
        <div class="asteroids-gameover">
          {{ status() === 'won' ? 'SECTOR CLEARED!' : 'SHIP DESTROYED' }} — SCORE {{ score() }} — PRESS ENTER TO RESTART
        </div>
      } @else {
        <div class="asteroids-hint">ARROWS/WASD ROTATE + THRUST — SPACE TO FIRE — ESC TO EXIT</div>
      }
    </div>
  `,
  styles: [`
    :host {
      position: fixed;
      inset: 0;
      z-index: 9999;
      display: flex;
      align-items: center;
      justify-content: center;
      padding: 16px;
      background: rgba(0, 0, 0, 0.85);
      overflow: auto;
    }

    .asteroids-panel {
      display: flex;
      flex-direction: column;
      align-items: center;
      gap: 10px;
      padding: 16px;
      background: #010c05;
      border: 2px solid #33ff66;
      border-radius: 4px;
      box-shadow: 0 0 24px rgba(51, 255, 102, 0.45);
      font-family: 'IBM Plex Mono', 'Courier New', monospace;
      color: #33ff66;
    }

    .asteroids-header {
      display: flex;
      align-items: center;
      gap: 18px;
      width: 100%;
      justify-content: space-between;
      flex-wrap: wrap;
    }

    .asteroids-title {
      font-weight: bold;
      letter-spacing: 2px;
      color: #ffb000;
      text-shadow: 0 0 6px rgba(255, 176, 0, 0.7);
    }

    .asteroids-score,
    .asteroids-lives {
      font-size: 0.9rem;
    }

    .asteroids-close {
      background: transparent;
      border: 1px solid #33ff66;
      color: #33ff66;
      font-family: inherit;
      padding: 4px 10px;
      cursor: pointer;
      border-radius: 2px;
    }

    .asteroids-close:hover {
      background: rgba(51, 255, 102, 0.15);
    }

    .asteroids-canvas {
      width: auto;
      height: min(360px, calc(100vh - 180px));
      max-width: calc(100vw - 64px);
      image-rendering: pixelated;
      background: #000;
      border: 1px solid rgba(51, 255, 102, 0.5);
    }

    .asteroids-gameover,
    .asteroids-hint {
      font-size: 0.85rem;
      text-align: center;
      opacity: 0.9;
    }
  `]
})
export class AsteroidsGame implements OnInit, OnDestroy {
  private readonly canvasRef = viewChild.required<ElementRef<HTMLCanvasElement>>('canvasEl');
  private ctx: CanvasRenderingContext2D | null = null;
  private intervalId: ReturnType<typeof setInterval> | undefined;
  private readonly pressedKeys = new Set<string>();
  private lastShotAt = 0;
  private invulnTicks = 0;

  readonly canvasWidth = CANVAS_WIDTH;
  readonly canvasHeight = CANVAS_HEIGHT;

  readonly close = output<void>();
  readonly score = signal(0);
  readonly lives = signal(START_LIVES);
  readonly status = signal<GameStatus>('playing');

  private ship = { x: CANVAS_WIDTH / 2, y: CANVAS_HEIGHT / 2, angle: -Math.PI / 2, vx: 0, vy: 0 };
  private bullets: Bullet[] = [];
  private asteroids: Asteroid[] = [];

  ngOnInit(): void {
    this.resetState();
    const canvas = this.canvasRef().nativeElement;
    this.ctx = canvas.getContext('2d');
    this.intervalId = setInterval(() => this.tick(), TICK_MS);
  }

  ngOnDestroy(): void {
    if (this.intervalId !== undefined) {
      clearInterval(this.intervalId);
    }
  }

  onKeydown(event: KeyboardEvent): void {
    if (event.key === 'Escape') {
      this.requestClose();
      return;
    }
    if (event.key === 'Enter' && this.status() !== 'playing') {
      this.restart();
      return;
    }
    if (event.code === 'Space') {
      event.preventDefault();
      this.shoot();
      return;
    }
    const key = event.key.toLowerCase();
    if (MOVE_KEYS.includes(key)) {
      event.preventDefault();
      this.pressedKeys.add(key);
    }
  }

  onKeyup(event: KeyboardEvent): void {
    this.pressedKeys.delete(event.key.toLowerCase());
  }

  requestClose(): void {
    this.close.emit();
  }

  private restart(): void {
    this.resetState();
  }

  private resetState(): void {
    this.ship = { x: CANVAS_WIDTH / 2, y: CANVAS_HEIGHT / 2, angle: -Math.PI / 2, vx: 0, vy: 0 };
    this.bullets = [];
    this.asteroids = [];
    this.spawnWave(5);
    this.score.set(0);
    this.lives.set(START_LIVES);
    this.status.set('playing');
    this.pressedKeys.clear();
    this.invulnTicks = INVULN_TICKS;
  }

  private spawnWave(count: number): void {
    for (let i = 0; i < count; i++) {
      let x: number;
      let y: number;
      do {
        x = Math.random() * CANVAS_WIDTH;
        y = Math.random() * CANVAS_HEIGHT;
      } while (Math.hypot(x - this.ship.x, y - this.ship.y) < 60);
      this.asteroids.push(this.createAsteroid(x, y, 'large'));
    }
  }

  private createAsteroid(x: number, y: number, size: AsteroidSize): Asteroid {
    const speed = SIZE_SPEED[size];
    const dir = Math.random() * Math.PI * 2;
    const jitters = Array.from({ length: 8 }, () => 0.75 + Math.random() * 0.5);
    return {
      x,
      y,
      vx: Math.cos(dir) * speed * (0.5 + Math.random()),
      vy: Math.sin(dir) * speed * (0.5 + Math.random()),
      size,
      radius: SIZE_RADIUS[size],
      angle: Math.random() * Math.PI * 2,
      spin: (Math.random() - 0.5) * 0.05,
      jitters
    };
  }

  private tick(): void {
    if (this.status() === 'playing') {
      this.updateShip();
      this.updateBullets();
      this.updateAsteroids();
      this.checkCollisions();
      this.checkEndConditions();
      if (this.invulnTicks > 0) {
        this.invulnTicks--;
      }
    }
    this.draw();
  }

  private updateShip(): void {
    if (this.pressedKeys.has('arrowleft') || this.pressedKeys.has('a')) {
      this.ship.angle -= ROT_SPEED;
    }
    if (this.pressedKeys.has('arrowright') || this.pressedKeys.has('d')) {
      this.ship.angle += ROT_SPEED;
    }
    if (this.pressedKeys.has('arrowup') || this.pressedKeys.has('w')) {
      this.ship.vx += Math.cos(this.ship.angle) * THRUST;
      this.ship.vy += Math.sin(this.ship.angle) * THRUST;
    }
    this.ship.vx *= FRICTION;
    this.ship.vy *= FRICTION;
    const speed = Math.hypot(this.ship.vx, this.ship.vy);
    if (speed > MAX_SPEED) {
      this.ship.vx = (this.ship.vx / speed) * MAX_SPEED;
      this.ship.vy = (this.ship.vy / speed) * MAX_SPEED;
    }
    this.ship.x = this.wrap(this.ship.x + this.ship.vx, CANVAS_WIDTH);
    this.ship.y = this.wrap(this.ship.y + this.ship.vy, CANVAS_HEIGHT);
  }

  private updateBullets(): void {
    this.bullets = this.bullets
      .map((bullet) => ({
        ...bullet,
        x: this.wrap(bullet.x + bullet.vx, CANVAS_WIDTH),
        y: this.wrap(bullet.y + bullet.vy, CANVAS_HEIGHT),
        life: bullet.life - 1
      }))
      .filter((bullet) => bullet.life > 0);
  }

  private updateAsteroids(): void {
    for (const asteroid of this.asteroids) {
      asteroid.x = this.wrap(asteroid.x + asteroid.vx, CANVAS_WIDTH);
      asteroid.y = this.wrap(asteroid.y + asteroid.vy, CANVAS_HEIGHT);
      asteroid.angle += asteroid.spin;
    }
  }

  private wrap(value: number, max: number): number {
    if (value < 0) {
      return value + max;
    }
    if (value > max) {
      return value - max;
    }
    return value;
  }

  private shoot(): void {
    if (this.status() !== 'playing') {
      return;
    }
    const now = Date.now();
    if (now - this.lastShotAt < SHOOT_COOLDOWN_MS) {
      return;
    }
    this.lastShotAt = now;
    this.bullets.push({
      x: this.ship.x + Math.cos(this.ship.angle) * SHIP_SIZE,
      y: this.ship.y + Math.sin(this.ship.angle) * SHIP_SIZE,
      vx: Math.cos(this.ship.angle) * BULLET_SPEED + this.ship.vx,
      vy: Math.sin(this.ship.angle) * BULLET_SPEED + this.ship.vy,
      life: BULLET_LIFE
    });
  }

  private checkCollisions(): void {
    const survivingAsteroids: Asteroid[] = [];
    const spentBullets = new Set<Bullet>();

    for (const asteroid of this.asteroids) {
      let destroyed = false;
      for (const bullet of this.bullets) {
        if (spentBullets.has(bullet)) {
          continue;
        }
        if (Math.hypot(bullet.x - asteroid.x, bullet.y - asteroid.y) < asteroid.radius) {
          spentBullets.add(bullet);
          destroyed = true;
          this.score.update((s) => s + SIZE_POINTS[asteroid.size]);
          const nextSize = NEXT_SIZE[asteroid.size];
          if (nextSize) {
            survivingAsteroids.push(this.createAsteroid(asteroid.x, asteroid.y, nextSize));
            survivingAsteroids.push(this.createAsteroid(asteroid.x, asteroid.y, nextSize));
          }
          break;
        }
      }
      if (!destroyed) {
        survivingAsteroids.push(asteroid);
      }
    }

    this.asteroids = survivingAsteroids;
    this.bullets = this.bullets.filter((bullet) => !spentBullets.has(bullet));

    if (this.invulnTicks <= 0) {
      for (const asteroid of this.asteroids) {
        if (Math.hypot(this.ship.x - asteroid.x, this.ship.y - asteroid.y) < asteroid.radius + SHIP_SIZE / 2) {
          this.lives.update((l) => l - 1);
          this.ship = { x: CANVAS_WIDTH / 2, y: CANVAS_HEIGHT / 2, angle: -Math.PI / 2, vx: 0, vy: 0 };
          this.invulnTicks = INVULN_TICKS;
          break;
        }
      }
    }
  }

  private checkEndConditions(): void {
    if (this.lives() <= 0) {
      this.status.set('lost');
    } else if (this.asteroids.length === 0) {
      this.status.set('won');
    }
  }

  private draw(): void {
    const ctx = this.ctx;
    if (!ctx) {
      return;
    }

    ctx.fillStyle = '#010c05';
    ctx.fillRect(0, 0, CANVAS_WIDTH, CANVAS_HEIGHT);

    ctx.strokeStyle = this.invulnTicks > 0 && this.invulnTicks % 10 < 5 ? 'rgba(51, 255, 102, 0.3)' : '#33ff66';
    ctx.lineWidth = 1.5;
    ctx.save();
    ctx.translate(this.ship.x, this.ship.y);
    ctx.rotate(this.ship.angle);
    ctx.beginPath();
    ctx.moveTo(SHIP_SIZE, 0);
    ctx.lineTo(-SHIP_SIZE * 0.7, SHIP_SIZE * 0.6);
    ctx.lineTo(-SHIP_SIZE * 0.3, 0);
    ctx.lineTo(-SHIP_SIZE * 0.7, -SHIP_SIZE * 0.6);
    ctx.closePath();
    ctx.stroke();
    if (this.pressedKeys.has('arrowup') || this.pressedKeys.has('w')) {
      ctx.strokeStyle = '#ffb000';
      ctx.beginPath();
      ctx.moveTo(-SHIP_SIZE * 0.3, 0);
      ctx.lineTo(-SHIP_SIZE * 1.3, 0);
      ctx.stroke();
    }
    ctx.restore();

    ctx.fillStyle = '#ffb000';
    for (const bullet of this.bullets) {
      ctx.fillRect(bullet.x - 1, bullet.y - 1, 2, 2);
    }

    ctx.strokeStyle = '#33ff66';
    for (const asteroid of this.asteroids) {
      ctx.save();
      ctx.translate(asteroid.x, asteroid.y);
      ctx.rotate(asteroid.angle);
      ctx.beginPath();
      const points = asteroid.jitters.length;
      for (let i = 0; i < points; i++) {
        const a = (i / points) * Math.PI * 2;
        const r = asteroid.radius * asteroid.jitters[i];
        const px = Math.cos(a) * r;
        const py = Math.sin(a) * r;
        if (i === 0) {
          ctx.moveTo(px, py);
        } else {
          ctx.lineTo(px, py);
        }
      }
      ctx.closePath();
      ctx.stroke();
      ctx.restore();
    }
  }
}
